import _ from 'lodash';
import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { eventTypeConvert } from '@/shared/lib/aiUtils';
import { ThumbnailEvent } from '@/entities/event';
import useRhythmEventPreviewStrip from './useRhythmEventPreviewStrip';

export type RhythmEventPreviewStripProps = {
  id: string;
  keyRecoil: string;
  activeButton: any;
  overlayContent?: React.ReactNode;
  event: any;
  isSelected?: boolean;
  isActive?: boolean;
  channels: any;
  highPass: any;
  width: number;
  height: number;
  gain: any;
  timezoneOffset: number;
  startReport: any;
  ecgDataMap: any;
  index: any;
  customOffset: any;
  onClickSelectEvent: (event: any, index: any) => void;
  // some callers also pass these, but the component never reads them - pre-existing.
};

/**
 * Thumbnail preview of one rhythm or beat event inside a strip list. Feeds the hook's resolved ECG samples, beat markers, channels, duration and reviewed or edited flags into the shared thumbnail, and adds a tooltip warning when the event carries changes that have not been applied yet.
 *
 * @param props - The preview props, supplying the event, thumbnail id, size, gain, high pass, selection flags and timezone offset.
 * @returns The rendered event thumbnail, plus an unapplied-change tooltip when the event was edited.
 */
const RhythmEventPreviewStrip = ({
  id,
  keyRecoil,
  activeButton,
  overlayContent: overlayContentProp,
  event,
  isSelected,
  isActive,
  channels: channelsProp,
  highPass,
  width,
  height,
  gain,
  timezoneOffset,
  startReport,
  ecgDataMap,
  index,
  customOffset,
  onClickSelectEvent,
}: RhythmEventPreviewStripProps) => {
  const {
    overlayContent,
    isNoHrEvent,
    isBeatEvent,
    effectiveType,
    isShowCountBeats,
    baseEvent,
    onClick,
    isEdited,
    groupReviewedState,
    ecgData,
    beatMarkers,
    hesBeatStatus,
    channels,
    totalDuration,
    typeRightSource,
  } = useRhythmEventPreviewStrip({
    keyRecoil,
    activeButton,
    overlayContent: overlayContentProp,
    event,
    channels: channelsProp,
    index,
    ecgDataMap,
    customOffset,
    onClickSelectEvent,
  });

  return (
    <>
      <ThumbnailEvent
        id={id}
        isNoHrEvent={isNoHrEvent}
        isBeatEvent={isBeatEvent}
        eventType={effectiveType}
        countBeats={isShowCountBeats ? (baseEvent?.countBeats ?? 0) : 0}
        overlayContent={overlayContent}
        onClickSelect={onClick as any}
        isSelected={isSelected}
        isActive={isActive}
        isCaptured={baseEvent?.captures?.length > 0}
        isChanged={isEdited}
        isReviewed={!_.isNil(groupReviewedState) ? groupReviewedState : baseEvent?.isReviewed}
        ecgData={ecgData}
        beatMarkers={beatMarkers}
        hesBeatStatus={hesBeatStatus}
        channels={channels}
        highPass={highPass}
        width={width}
        height={height}
        gain={gain}
        time={baseEvent?.start || event.start}
        duration={totalDuration}
        typeRight={(eventTypeConvert as any)[typeRightSource]}
        timezoneOffset={timezoneOffset}
        startReport={startReport}
        maxHr={baseEvent?.maxHr}
        minHr={baseEvent?.minHr}
        avgHr={baseEvent?.avgHr}
      />
      {isEdited && id && (
        <UncontrolledTooltip
          className="custom-tooltip"
          target={id}
          modifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
          placement="bottom"
          delay={{ show: 0, hide: 0 }}
          hideArrow
        >
          The change has not been applied
        </UncontrolledTooltip>
      )}
    </>
  );
};

export default RhythmEventPreviewStrip;
